import { createElement, useEffect, useMemo, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { AlertTriangle, Copy, Loader2, Sparkles, Trash2, Upload, User, Users } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { WorkspaceIntegrationsSection } from '../components/WorkspaceIntegrationsSection'
import { SuperAgentTokenSection } from '../components/SuperAgentTokenSection'
import type { LucideIcon } from 'lucide-react'
import { usePageLayout } from '@/hooks/usePageLayout'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import SettingsSidebar from '@/components/app/sidebars/settings'
import {
  SettingsCard,
  SettingsPage,
  SettingsPageLoading,
  SettingsRow,
  SettingsSaveBar,
  SettingsSection,
  settingsInputClass,
} from '@/components/app/settings'
import useLoginStore from '@/store/LoginStore'
import { switchToPersonal, updateAiSettings, updateWorkspace, uploadWorkspaceLogo } from '@/lib/api/features/workspace-settings'
import { cn } from '@/lib/utils'
import { useStatuses } from '@/lib/db/rxdb/hooks/entities/statuses'
import { useTasks } from '@/lib/db/rxdb/hooks/entities/tasks'
import { PROGRESS_TYPES } from '@/lib/types/subscription'
import { useIsApiDown } from '@/lib/api/useIsApiDown'
import { FEATURE_FLAGS } from '@/lib/constants/featureFlags'

type ApiErrorShape = {
  response?: { data?: { message?: string; error?: string } }
  message?: string
}

/** Best message from an axios-style error, or the fallback. */
function apiErrorMessage(error: unknown, fallback: string): string {
  const e = error as ApiErrorShape | null
  return e?.response?.data?.message ?? e?.response?.data?.error ?? fallback
}

const WORKSPACE_TYPE = { PERSONAL: 1, TEAM: 2 } as const

export default function WorkspaceSettingsPage() {
  const { t } = useTranslation('settings')
  const { t: tCommon } = useTranslation('common')
  usePageLayout({
    sidebarContent: createElement(SettingsSidebar),
    showHeader: false,
  })

  const workspaceFormSchema = useMemo(
    () =>
      z.object({
        name: z
          .string()
          .trim()
          .min(1, t('workspaceSettings.validation.nameRequired'))
          .max(30, t('workspaceSettings.validation.nameMax')),
        workspaceSlug: z
          .string()
          .min(1, t('workspaceSettings.validation.slugRequired'))
          .max(50, t('workspaceSettings.validation.slugMax'))
          .regex(
            /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
            t('workspaceSettings.validation.slugPattern'),
          ),
        description: z
          .string()
          .max(500, t('workspaceSettings.validation.descriptionMax'))
          .optional()
          .or(z.literal('')),
      }),
    [t],
  )
  type WorkspaceFormData = z.infer<typeof workspaceFormSchema>

  const workspaceSlug = useLoginStore((s) => s.workspaceSlug)
  const workspaces = useLoginStore((s) => s.workspaces)
  const setWorkspaces = useLoginStore((s) => s.setWorkspaces)
  const workspaceType = useLoginStore((s) => s.workspaceType)
  const setWorkspaceType = useLoginStore((s) => s.setWorkspaceType)

  const currentWorkspace = workspaces.find(
    (w) => w.workspaceSlug === workspaceSlug,
  )

  const [logoUrl, setLogoUrl] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [isSwitchingMode, setIsSwitchingMode] = useState(false)
  const [isTogglingAi, setIsTogglingAi] = useState(false)
  const aiEnabled = currentWorkspace?.aiEnabled ?? false

  const handleToggleAiEnabled = async (nextEnabled: boolean) => {
    if (isTogglingAi) return
    setIsTogglingAi(true)
    // Optimistic store update so the toggle moves immediately. Realtime broadcast
    // confirms or corrects via sharedWebSocketManager.
    setWorkspaces(
      workspaces.map((w) =>
        w.workspaceSlug === workspaceSlug ? { ...w, aiEnabled: nextEnabled } : w,
      ),
    )
    try {
      await updateAiSettings(workspaceSlug, { aiEnabled: nextEnabled })
      toast.success(
        nextEnabled
          ? t('workspaceSettings.ai.enabledToast')
          : t('workspaceSettings.ai.disabledToast'),
      )
    } catch (err: unknown) {
      // Roll back optimistic update on failure.
      setWorkspaces(
        workspaces.map((w) =>
          w.workspaceSlug === workspaceSlug ? { ...w, aiEnabled: !nextEnabled } : w,
        ),
      )
      const e = err as ApiErrorShape | null
      toast.error(
        e?.response?.data?.message || e?.message || t('workspaceSettings.ai.updateFailed'),
      )
    } finally {
      setIsTogglingAi(false)
    }
  }
  const isApiDown = useIsApiDown()
  const offlineTitle = isApiDown ? t('workspaceSettings.offlineTitle') : undefined
  const [reassignDialogOpen, setReassignDialogOpen] = useState(false)
  const [reassignments, setReassignments] = useState<Record<string, 'BL' | 'CD'>>({})
  const [switchToTeamDialogOpen, setSwitchToTeamDialogOpen] = useState(false)

  // Status data for reassignment dialog
  const { statuses: allStatuses } = useStatuses()
  const { tasks } = useTasks()

  const advancedStatuses = useMemo(
    () =>
      allStatuses.filter((s) => {
        return s.progressType !== PROGRESS_TYPES.BACKLOG && s.progressType !== PROGRESS_TYPES.COMPLETED
      }),
    [allStatuses],
  )

  const taskCountsByStatusId = useMemo(() => {
    const counts: Record<string, number> = {}
    for (const task of tasks) {
      if (task.statusId) {
        counts[task.statusId] = (counts[task.statusId] || 0) + 1
      }
    }
    return counts
  }, [tasks])

  const PROGRESS_TYPE_LABELS: Record<string, string> = {
    [PROGRESS_TYPES.BACKLOG]: t('workspaceSettings.progressTypes.BL'),
    [PROGRESS_TYPES.PLANNED]: t('workspaceSettings.progressTypes.PL'),
    [PROGRESS_TYPES.IN_PROGRESS]: t('workspaceSettings.progressTypes.IP'),
    [PROGRESS_TYPES.COMPLETED]: t('workspaceSettings.progressTypes.CD'),
    [PROGRESS_TYPES.CANCELLED]: t('workspaceSettings.progressTypes.CL'),
  }

  const form = useForm<WorkspaceFormData>({
    resolver: zodResolver(workspaceFormSchema),
    defaultValues: {
      name: '',
      workspaceSlug: '',
      description: '',
    },
  })

  const savedValues = useMemo(
    () =>
      currentWorkspace
        ? {
            name: currentWorkspace.name,
            workspaceSlug: currentWorkspace.workspaceSlug,
            description: currentWorkspace.description ?? '',
          }
        : null,
    [currentWorkspace],
  )

  // Load workspace data into the form. keepDirtyValues: a background refresh
  // of the workspace list (logo upload, realtime) must not wipe an edit the
  // user hasn't saved yet.
  useEffect(() => {
    if (savedValues) {
      form.reset(savedValues, { keepDirtyValues: true })
    }
  }, [savedValues, form])

  useEffect(() => {
    if (currentWorkspace) setLogoUrl(currentWorkspace.logoUrl)
  }, [currentWorkspace])

  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    // Reset so choosing the same file again still fires onChange.
    e.target.value = ''
    if (!file) return

    if (file.size > 2 * 1024 * 1024) {
      toast.error(t('workspaceSettings.toast.logoSizeError'))
      return
    }

    if (!file.type.startsWith('image/')) {
      toast.error(t('workspaceSettings.toast.logoTypeError'))
      return
    }

    setIsUploading(true)
    try {
      // Show preview immediately
      const reader = new FileReader()
      reader.onloadend = () => {
        setLogoUrl(reader.result as string)
      }
      reader.readAsDataURL(file)

      // Upload to server
      const result = await uploadWorkspaceLogo(workspaceSlug, file)
      setLogoUrl(result.url)

      // Update cached workspace list
      setWorkspaces(
        workspaces.map((w) =>
          w.workspaceSlug === workspaceSlug ? { ...w, logoUrl: result.url } : w,
        ),
      )
      toast.success(t('workspaceSettings.toast.logoUploaded'))
    } catch {
      toast.error(t('workspaceSettings.toast.logoUploadFailed'))
      setLogoUrl(currentWorkspace?.logoUrl ?? null)
    } finally {
      setIsUploading(false)
    }
  }

  const handleRemoveLogo = async () => {
    try {
      await updateWorkspace(workspaceSlug, { logoUrl: '' })
      setLogoUrl(null)

      // Update cached workspace list
      setWorkspaces(
        workspaces.map((w) =>
          w.workspaceSlug === workspaceSlug ? { ...w, logoUrl: null } : w,
        ),
      )
      toast.success(t('workspaceSettings.toast.logoRemoved'))
    } catch {
      toast.error(t('workspaceSettings.toast.logoRemoveFailed'))
    }
  }

  const handleModeChange = async (newType: number) => {
    if (newType === workspaceType || isSwitchingMode) return

    if (newType === WORKSPACE_TYPE.PERSONAL) {
      // Switching to Personal — check if there are tasks on non-simple statuses
      const hasAdvancedTasks = advancedStatuses.some(
        (s) => (taskCountsByStatusId[s.id] ?? 0) > 0,
      )

      if (hasAdvancedTasks || advancedStatuses.length > 0) {
        // Open reassignment dialog
        const initial: Record<string, 'BL' | 'CD'> = {}
        for (const status of advancedStatuses) {
          const pt = status.progressType
          initial[status.id] = pt === PROGRESS_TYPES.CANCELLED ? 'CD' : 'BL'
        }
        setReassignments(initial)
        setReassignDialogOpen(true)
        return
      }

      // No advanced statuses — switch directly
      setIsSwitchingMode(true)
      try {
        const result = await switchToPersonal(workspaceSlug, { reassignments: [] })
        setWorkspaceType(result.workspaceType)
        setWorkspaces(
          workspaces.map((w) =>
            w.workspaceSlug === workspaceSlug
              ? { ...w, workspaceType: result.workspaceType }
              : w,
          ),
        )
        toast.success(t('workspaceSettings.toast.switchedPersonal'))
      } catch {
        toast.error(t('workspaceSettings.toast.switchModeFailed'))
      } finally {
        setIsSwitchingMode(false)
      }
      return
    }

    // Switching to Team — confirm before applying
    setSwitchToTeamDialogOpen(true)
  }

  const handleConfirmSwitchToTeam = async () => {
    setIsSwitchingMode(true)
    try {
      await updateWorkspace(workspaceSlug, { workspaceType: WORKSPACE_TYPE.TEAM })
      setWorkspaceType(WORKSPACE_TYPE.TEAM)
      setWorkspaces(
        workspaces.map((w) =>
          w.workspaceSlug === workspaceSlug
            ? { ...w, workspaceType: WORKSPACE_TYPE.TEAM }
            : w,
        ),
      )
      setSwitchToTeamDialogOpen(false)
      toast.success(t('workspaceSettings.toast.switchedTeam'))
    } catch {
      toast.error(t('workspaceSettings.toast.switchModeFailed'))
    } finally {
      setIsSwitchingMode(false)
    }
  }

  const handleConfirmSwitchToPersonal = async () => {
    setIsSwitchingMode(true)
    try {
      const reassignmentList = Object.entries(reassignments).map(
        ([statusId, targetProgressType]) => ({ statusId, targetProgressType }),
      )
      const result = await switchToPersonal(workspaceSlug, {
        reassignments: reassignmentList,
      })
      setWorkspaceType(result.workspaceType)
      setWorkspaces(
        workspaces.map((w) =>
          w.workspaceSlug === workspaceSlug
            ? { ...w, workspaceType: result.workspaceType }
            : w,
        ),
      )
      setReassignDialogOpen(false)
      toast.success(result.message)
    } catch (error: unknown) {
      toast.error(apiErrorMessage(error, t('workspaceSettings.toast.switchPersonalFailed')))
    } finally {
      setIsSwitchingMode(false)
    }
  }

  const onSubmit = async (data: WorkspaceFormData) => {
    try {
      const result = await updateWorkspace(workspaceSlug, {
        name: data.name,
        description: data.description || null,
      })

      // Update cached workspace list
      setWorkspaces(
        workspaces.map((w) =>
          w.workspaceSlug === workspaceSlug
            ? {
                ...w,
                name: result.name,
                description: result.description,
                logoUrl: result.logoUrl,
              }
            : w,
        ),
      )
      // Saved values become the new baseline, which hides the save bar.
      form.reset({
        name: result.name,
        workspaceSlug: data.workspaceSlug,
        description: result.description ?? '',
      })

      toast.success(t('workspaceSettings.toast.updated'))
    } catch (error: unknown) {
      toast.error(apiErrorMessage(error, t('workspaceSettings.toast.updateFailed')))
    }
  }

  const handleDiscard = () => {
    if (savedValues) form.reset(savedValues)
  }

  const workspaceUrlPrefix = `${window.location.host}/`
  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/${workspaceSlug}`)
      toast.success(t('workspaceSettings.fields.urlCopied'))
    } catch {
      toast.error(t('workspaceSettings.fields.urlCopyFailed'))
    }
  }

  if (!currentWorkspace) {
    return <SettingsPageLoading />
  }

  const nameError = form.formState.errors.name?.message
  const descriptionError = form.formState.errors.description?.message

  const modeOptions: Array<{
    type: number
    icon: LucideIcon
    title: string
    hint: string
  }> = [
    {
      type: WORKSPACE_TYPE.PERSONAL,
      icon: User,
      title: t('workspaceSettings.mode.personal'),
      hint: t('workspaceSettings.mode.personalHint'),
    },
    {
      type: WORKSPACE_TYPE.TEAM,
      icon: Users,
      title: t('workspaceSettings.mode.team'),
      hint: t('workspaceSettings.mode.teamHint'),
    },
  ]

  return (
    <SettingsPage
      title={t('workspaceSettings.title')}
      description={t('workspaceSettings.description')}
    >
      {/* Migration in progress banner */}
      {currentWorkspace.statusMigrationInProgress && (
        <div className="flex items-start gap-2 rounded-[14px] border border-amber-500/30 bg-amber-500/10 p-3">
          <AlertTriangle className="size-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
          <p className="text-xs text-amber-900 dark:text-amber-200">
            {t('workspaceSettings.migrationBanner')}
          </p>
        </div>
      )}

      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="contents"
        aria-label={t('workspaceSettings.title')}
      >
        <SettingsCard>
          {/* Logo */}
          <SettingsRow
            label={t('workspaceSettings.logo.label')}
            description={t('workspaceSettings.logo.hint')}
          >
            <div className="flex items-center gap-2">
              <div className="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-[10px] bg-foreground text-background">
                {isUploading ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : logoUrl ? (
                  <img
                    src={logoUrl}
                    alt={t('workspaceSettings.logo.alt')}
                    className="size-full object-cover"
                  />
                ) : (
                  <span className="text-sm font-semibold">
                    {currentWorkspace.name.charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
              <label
                htmlFor="workspace-logo"
                title={offlineTitle}
                className={cn(
                  'inline-flex h-9 items-center gap-1.5 rounded-lg border bg-card px-3 text-sm font-medium shadow-xs transition-colors',
                  isApiDown || isUploading
                    ? 'cursor-not-allowed opacity-50'
                    : 'cursor-pointer hover:bg-accent',
                )}
              >
                <Upload className="size-3.5" />
                {t('workspaceSettings.logo.upload')}
              </label>
              <input
                id="workspace-logo"
                type="file"
                accept="image/*"
                className="sr-only"
                onChange={handleLogoChange}
                disabled={isUploading || isApiDown}
              />
              {logoUrl && (
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="size-9 rounded-lg"
                  onClick={handleRemoveLogo}
                  disabled={isApiDown}
                  title={offlineTitle ?? t('workspaceSettings.logo.remove')}
                  aria-label={t('workspaceSettings.logo.remove')}
                >
                  <Trash2 className="size-3.5" />
                </Button>
              )}
            </div>
          </SettingsRow>

          {/* Name */}
          <SettingsRow
            label={t('workspaceSettings.fields.name')}
            htmlFor="workspace-name"
            control="field"
            error={nameError}
          >
            <input
              id="workspace-name"
              {...form.register('name')}
              placeholder={t('workspaceSettings.fields.namePlaceholder')}
              aria-invalid={nameError ? true : undefined}
              className={settingsInputClass}
            />
          </SettingsRow>

          {/* URL — read-only for now */}
          <SettingsRow
            label={t('workspaceSettings.fields.url')}
            description={t('workspaceSettings.fields.urlDescription')}
            htmlFor="workspace-slug"
            control="field"
          >
            <div className="flex h-9 w-full items-stretch overflow-hidden rounded-lg border border-input bg-secondary text-sm">
              <span className="flex max-w-[45%] items-center truncate border-r border-input px-2.5 font-mono text-xs text-muted-foreground">
                {workspaceUrlPrefix}
              </span>
              <input
                id="workspace-slug"
                {...form.register('workspaceSlug')}
                readOnly
                aria-readonly="true"
                tabIndex={-1}
                className="min-w-0 flex-1 cursor-default bg-transparent px-2.5 text-muted-foreground focus-visible:outline-none"
              />
              <button
                type="button"
                onClick={handleCopyUrl}
                className="flex items-center px-2.5 text-muted-foreground transition-colors hover:text-foreground"
                aria-label={t('workspaceSettings.fields.urlCopy')}
                title={t('workspaceSettings.fields.urlCopy')}
              >
                <Copy className="size-3.5" />
              </button>
            </div>
          </SettingsRow>

          {/* Description */}
          <SettingsRow
            label={t('workspaceSettings.fields.description')}
            description={t('workspaceSettings.fields.descriptionHint')}
            htmlFor="workspace-description"
            control="field"
            error={descriptionError}
          >
            <input
              id="workspace-description"
              {...form.register('description')}
              placeholder={t('workspaceSettings.fields.descriptionPlaceholder')}
              aria-invalid={descriptionError ? true : undefined}
              className={settingsInputClass}
            />
          </SettingsRow>
        </SettingsCard>

        {/* Workspace mode — independent of the form; switching asks first */}
        <SettingsSection
          title={t('workspaceSettings.mode.label')}
          aside={t('workspaceSettings.mode.keepsData')}
        >
          <div
            role="radiogroup"
            aria-label={t('workspaceSettings.mode.label')}
            className="grid grid-cols-1 gap-3 sm:grid-cols-2"
          >
            {modeOptions.map((option) => {
              const selected = workspaceType === option.type
              const Icon = option.icon
              return (
                <button
                  key={option.type}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  disabled={isSwitchingMode || isApiDown}
                  title={offlineTitle}
                  onClick={() => handleModeChange(option.type)}
                  className={cn(
                    'flex items-center gap-3 rounded-[14px] border bg-card p-4 text-left transition-colors disabled:cursor-not-allowed',
                    selected
                      ? 'border-foreground ring-1 ring-foreground'
                      : 'hover:bg-accent/60',
                  )}
                >
                  <span
                    className={cn(
                      'flex size-9 shrink-0 items-center justify-center rounded-lg',
                      selected
                        ? 'bg-brand-tint text-brand-tint-foreground'
                        : 'bg-secondary text-muted-foreground',
                    )}
                  >
                    <Icon className="size-4" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold">{option.title}</span>
                    <span className="block text-xs text-muted-foreground">{option.hint}</span>
                  </span>
                  <span
                    aria-hidden
                    className={cn(
                      'flex size-4 shrink-0 items-center justify-center rounded-full border',
                      selected ? 'border-foreground bg-foreground' : 'border-input',
                    )}
                  >
                    {selected && <span className="size-1.5 rounded-full bg-background" />}
                  </span>
                </button>
              )
            })}
          </div>
        </SettingsSection>

        {/* Integrations — workspace-level GitHub App connection (shared by all projects). */}
        {workspaceType === WORKSPACE_TYPE.TEAM && (
          FEATURE_FLAGS.ENABLE_GITHUB_INTEGRATION && (
            <WorkspaceIntegrationsSection workspaceSlug={workspaceSlug} />
          )
        )}

        {/* Super Agent bot token — workspace-level credential for the desktop orchestrator. */}
        {/* The section hides itself while Super Agent is off unless a token already
            exists, so an existing credential can still be revoked. */}
        {workspaceType === WORKSPACE_TYPE.TEAM && (
          <SuperAgentTokenSection workspaceSlug={workspaceSlug} />
        )}

        {/*
            AI features toggle — intentionally hidden for now.
            The `aiEnabled` column still gates every AI surface (Smart search, Ask,
            ExtractTasks). To enable for a specific workspace, run:
              UPDATE workspaces SET ai_enabled = true WHERE id = '<workspace-id>';
            Once we open it up to all workspaces, uncomment this block. Keeping the
            handler (`handleToggleAiEnabled`) and state (`isTogglingAi`) in place so
            we can re-enable with one revert.
        */}
        {false && (
          <SettingsCard>
            <SettingsRow
              label={
                <span className="flex items-center gap-2">
                  <Sparkles className="size-4 text-fuchsia-500" />
                  {t('workspaceSettings.ai.label')}
                </span>
              }
              description={t('workspaceSettings.ai.hint')}
            >
              <Switch
                checked={Boolean(aiEnabled)}
                disabled={isTogglingAi || isApiDown}
                onCheckedChange={handleToggleAiEnabled}
                aria-label={t('workspaceSettings.ai.label')}
              />
            </SettingsRow>
          </SettingsCard>
        )}

        {/* Name/description edits save together from the sticky bar. */}
        <SettingsSaveBar
          visible={form.formState.isDirty}
          isSaving={form.formState.isSubmitting}
          saveDisabled={isApiDown}
          saveTitle={offlineTitle}
          onDiscard={handleDiscard}
        />
      </form>

      {/* Reassignment Dialog */}
      <Dialog open={reassignDialogOpen} onOpenChange={setReassignDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('workspaceSettings.mode.switchPersonalTitle')}</DialogTitle>
            <DialogDescription>
              {t('workspaceSettings.mode.switchPersonalDescription')}
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-64 overflow-y-auto">
            {advancedStatuses.length > 0 ? (
              <div className="space-y-3">
                {advancedStatuses.map((status) => {
                  const pt = status.progressType
                  const count = taskCountsByStatusId[status.id] ?? 0
                  return (
                    <div
                      key={status.id}
                      className="flex items-center justify-between gap-3"
                    >
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <span className="text-sm font-medium truncate">
                          {status.name}
                        </span>
                        <Badge variant="outline" className="text-xs shrink-0">
                          {PROGRESS_TYPE_LABELS[pt] ?? pt}
                        </Badge>
                        {count > 0 && (
                          <Badge variant="secondary" className="text-xs shrink-0">
                            {t('workspaceSettings.mode.tasks', { count })}
                          </Badge>
                        )}
                      </div>
                      <Select
                        value={reassignments[status.id] ?? 'BL'}
                        onValueChange={(value) =>
                          setReassignments((prev) => ({
                            ...prev,
                            [status.id]: value as 'BL' | 'CD',
                          }))
                        }
                      >
                        <SelectTrigger className="w-32 h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="BL">{t('workspaceSettings.mode.reassignBacklog')}</SelectItem>
                          <SelectItem value="CD">{t('workspaceSettings.mode.reassignCompleted')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )
                })}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                {t('workspaceSettings.mode.noAdvancedStatuses')}
              </p>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setReassignDialogOpen(false)}
              disabled={isSwitchingMode}
            >
              {tCommon('actions.cancel')}
            </Button>
            <Button
              onClick={handleConfirmSwitchToPersonal}
              disabled={isSwitchingMode}
            >
              {isSwitchingMode ? t('workspaceSettings.mode.switching') : t('workspaceSettings.mode.switchPersonalCta')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Switch to Team confirmation */}
      <Dialog open={switchToTeamDialogOpen} onOpenChange={setSwitchToTeamDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('workspaceSettings.mode.switchTeamTitle')}</DialogTitle>
            <DialogDescription>
              {t('workspaceSettings.mode.switchTeamDescription')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSwitchToTeamDialogOpen(false)}
              disabled={isSwitchingMode}
            >
              {tCommon('actions.cancel')}
            </Button>
            <Button
              onClick={handleConfirmSwitchToTeam}
              disabled={isSwitchingMode}
            >
              {isSwitchingMode ? t('workspaceSettings.mode.switching') : t('workspaceSettings.mode.switchTeamCta')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SettingsPage>
  )
}

export const Route = createFileRoute(
  '/_AppWrapper/_AppLayout/$workspaceSlug/settings/workspace',
)({
  component: WorkspaceSettingsPage,
})
